import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, share } from 'rxjs/operators';

/** 定義一個內部介面來管理每個頻道的資源 */
interface ChannelResource {
  channel: BroadcastChannel; // 實例
  observable: Observable<unknown>; // Observable stream
  refCount: number; // 引用計數，用於判斷何時關閉 channel
}

@Injectable({
  providedIn: 'root',
})
export class BroadcastChannelService implements OnDestroy {
  /** 使用 Map 來儲存和管理所有已建立的頻道資源 */
  private channels = new Map<string, ChannelResource>();

  constructor() {}

  /**
   * 當服務被銷毀時（通常是應用程式關閉時），確保所有頻道都被關閉。
   */
  ngOnDestroy(): void {
    this.channels.forEach((_, channelName) => {
      this._closeChannel(channelName);
    });
  }

  /**
   * 建立一個新的頻道資源
   *
   * @private
   * @param channelName
   */
  private _createChannelResource<T>(channelName: string): void {
    const channel = new BroadcastChannel(channelName);

    // 建立一個 Subject，用於將 BroadcastChannel 的事件轉換為 Observable Stream
    const subject = new Subject<T>();

    // 設定訊息監聽器
    channel.onmessage = (event: MessageEvent<T>) => {
      subject.next(event.data);
    };

    // 設定錯誤監聽器
    channel.onmessageerror = (event: MessageEvent) => {
      subject.error(event);
    };

    const observable = subject.asObservable().pipe(
      share(), // 使用 share() 確保所有訂閱者共享同一個來源，避免重複建立監聽器
    );

    this.channels.set(channelName, {
      channel,
      observable,
      refCount: 0,
    });
  }

  /**
   * 關閉並清理指定頻道的資源
   *
   * @private
   * @param channelName
   */
  private _closeChannel(channelName: string): void {
    const resource = this.channels.get(channelName);
    if (resource) {
      resource.channel.onmessage = null;
      resource.channel.onmessageerror = null;
      resource.channel.close(); // 關閉底層的 BroadcastChannel
      this.channels.delete(channelName); // 從 Map 中移除
    }
  }

  /**
   * 監聽指定頻道的訊息。
   *
   * @param channelName 要監聽的頻道名稱。
   * @returns 回傳一個 Observable，它會發出該頻道收到的所有訊息。
   */
  listen<T>(channelName: string): Observable<T> {
    // 檢查是否已經有這個頻道的資源
    if (!this.channels.has(channelName)) {
      this._createChannelResource<T>(channelName);
    }

    const resource = this.channels.get(channelName)!;
    resource.refCount++; // 增加引用計數

    // 回傳 observable，並在取消訂閱時減少引用計數
    return (resource.observable as Observable<T>).pipe(
      finalize(() => {
        resource.refCount--;

        // 如果沒有任何地方在監聽這個頻道了，就關閉並清理資源
        if (resource.refCount === 0) {
          this._closeChannel(channelName);
        }
      }),
    );
  }

  /**
   * 向指定頻道發布一條訊息。
   *
   * @param channelName 要發布訊息的頻道名稱。
   * @param message 要發送的訊息。
   * @description
   * - 發布訊息是一個短暫的操作，我們可以直接建立、發送、然後關閉。
   * - 這種 "fire-and-forget" 的模式更為穩健，不依賴於當前是否有監聽者。
   */
  publish<T>(channelName: string, message: T): void {
    const channel = new BroadcastChannel(channelName);
    channel.postMessage(message);
    channel.close();
  }
}
